import { useMemo, useRef, useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import clsx from 'clsx';
import { AnimatePresence, motion } from 'framer-motion';
import { MdKeyboardArrowDown } from 'react-icons/md';
import type { ActiveUser } from '../types/ActiveUser';

type Props = {
  activeUsers: ActiveUser[];
  selectedUserIds: number[];
  onSelect: (userIds: number[]) => void;
  currentUserId?: number;
  openOnHover?: boolean;
  emptyLabel?: string;
};

export default function UsersMenu({
  activeUsers,
  selectedUserIds,
  onSelect,
  currentUserId,
  openOnHover = false,
  emptyLabel = '未選択',
}: Props) {
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownPos, setDropdownPos] = useState<{ top?: number; bottom?: number; left: number }>({
    left: 0,
  });
  const buttonRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // ログインユーザーを先頭に表示
  const sortedUsers = useMemo(() => {
    if (!currentUserId) return activeUsers;
    return [...activeUsers].sort((a, b) => {
      if (a.id === currentUserId) return -1;
      if (b.id === currentUserId) return 1;
      return 0;
    });
  }, [activeUsers, currentUserId]);

  const selectedUsers = activeUsers
    .filter((user) => selectedUserIds.includes(user.id))
    .sort((a, b) => {
      if (a.id === currentUserId) return -1;
      if (b.id === currentUserId) return 1;
      return 0;
    });
  const additionalCount = selectedUsers.length - 1;

  const handleToggle = (userId: number) => {
    if (selectedUserIds.includes(userId)) {
      onSelect(selectedUserIds.filter((id) => id !== userId));
    } else {
      onSelect([...selectedUserIds, userId]);
    }
  };

  const DROPDOWN_MAX_HEIGHT = 240;
  const updateDropdownPos = () => {
    if (!buttonRef.current) return;
    const rect = buttonRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const left = rect.right - 160;
    if (spaceBelow < DROPDOWN_MAX_HEIGHT + 4) {
      setDropdownPos({ bottom: window.innerHeight - rect.top + 4, left });
    } else {
      setDropdownPos({ top: rect.bottom + 4, left });
    }
  };

  const handleOpen = () => {
    updateDropdownPos();
    setIsOpen(true);
  };

  // ボタン・ドロップダウン外クリックで閉じる
  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (
        buttonRef.current?.contains(e.target as Node) ||
        dropdownRef.current?.contains(e.target as Node)
      ) {
        return;
      }
      setIsOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  // スクロール時に閉じる（ドロップダウン内のスクロールは除く）
  useEffect(() => {
    if (!isOpen) return;
    const handleScroll = (e: Event) => {
      if (dropdownRef.current?.contains(e.target as Node)) return;
      setIsOpen(false);
    };
    window.addEventListener('scroll', handleScroll, true);
    return () => window.removeEventListener('scroll', handleScroll, true);
  }, [isOpen]);

  const dropdown = (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          ref={dropdownRef}
          initial={{ opacity: 0, y: -4, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: -4, scale: 0.95 }}
          transition={{ duration: 0.15, ease: 'easeOut' }}
          style={{
            position: 'fixed',
            top: dropdownPos.top,
            bottom: dropdownPos.bottom,
            left: dropdownPos.left,
          }}
          data-users-menu=""
          className="hidden-scrollbar pointer-events-auto z-[9999] max-h-[240px] w-[160px]! origin-top-right overflow-y-auto rounded bg-white shadow-[0px_0px_12px_0px_#00000033]"
          onMouseEnter={openOnHover ? () => setIsOpen(true) : undefined}
          onMouseLeave={openOnHover ? () => setIsOpen(false) : undefined}
        >
          {sortedUsers.map((user) => {
            const isSelected = selectedUserIds.includes(user.id);
            const isCurrentUser = user.id === currentUserId;
            return (
              <div
                key={user.id}
                className={clsx(
                  'flex h-xxl cursor-pointer items-center px-3 transition-colors duration-100 hover:bg-gray',
                  isSelected && 'bg-gray'
                )}
                onClick={(e) => {
                  e.stopPropagation();
                  handleToggle(user.id);
                }}
              >
                <span className="text-tag-sm">
                  {user.name}
                  {isCurrentUser && (
                    <span className="ml-1 text-[10px] text-placeholder">（自分）</span>
                  )}
                </span>
                <AnimatePresence>
                  {isSelected && (
                    <motion.img
                      initial={{ opacity: 0, scale: 0.5 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.5 }}
                      transition={{ duration: 0.15 }}
                      src="/agent/candidates/icon-check-circle.svg"
                      width={16}
                      height={16}
                      alt=""
                      className="ml-auto"
                    />
                  )}
                </AnimatePresence>
              </div>
            );
          })}
        </motion.div>
      )}
    </AnimatePresence>
  );

  return (
    <div
      className="relative"
      onMouseEnter={openOnHover ? handleOpen : undefined}
      onMouseLeave={openOnHover ? () => setIsOpen(false) : undefined}
    >
      <button
        ref={buttonRef}
        type="button"
        className={clsx('flex cursor-pointer items-center justify-center rounded-full bg-white')}
        onClick={(e) => {
          e.stopPropagation();
          if (isOpen) {
            setIsOpen(false);
          } else {
            handleOpen();
          }
        }}
      >
        <div
          className={clsx(
            'flex h-lg items-center rounded-full border border-solid border-border px-3 py-1 transition-colors duration-150',
            isOpen ? 'bg-tag-gray' : 'bg-white hover:bg-tag-gray'
          )}
        >
          <p className="text-xs text-nowrap">
            {selectedUsers.length === 0 ? (
              emptyLabel
            ) : additionalCount > 0 ? (
              <>
                {selectedUsers[0].name} <span className="text-primary">+{additionalCount}</span>
              </>
            ) : (
              selectedUsers[0].name
            )}
          </p>
          <motion.span
            animate={{ rotate: isOpen ? 180 : 0 }}
            transition={{ duration: 0.2, ease: 'easeInOut' }}
            className="flex items-center"
          >
            <MdKeyboardArrowDown size={20} className="opacity-50" />
          </motion.span>
        </div>
      </button>

      {createPortal(dropdown, document.body)}
    </div>
  );
}
